import { useCallback } from 'react';

import type { MediaType } from '../../editor/fields/media-type-config';

import { useMultiFileUploaderFlag } from './use-multi-file-uploader-flag';

// Large default is used to represent "unlimited" uploads
export const DEFAULT_MAX_FILES = 100000;

/**
 * Metadata returned from successful media upload
 */
export interface MediaUploadMetadata {
  id: string;
  url: string;
  created_at?: string;
  file_size?: number;
  content_type?: string;
  [key: string]: any; // Allow additional properties
}

/**
 * File information during/after upload
 */
export interface MediaUploadedFile {
  name: string;
  size: number;
  type: string;
  data?: File | Blob;
  [key: string]: any; // Allow additional properties
}

/**
 * Configuration for each media type including endpoints, file type restrictions, and field names
 */
interface MediaTypeConfig {
  allowedFileTypes: string[]; // Use ['*/*'] to accept any file type
  endpoint: string;
  fieldName: string;
}

/**
 * Configuration object defining upload settings for each media type.
 * These settings are used by useMediaFieldUploader to configure the UniversalMediaUploader.
 */
export const MEDIA_TYPE_SETTINGS: Record<MediaType, MediaTypeConfig> = {
  photo: {
    allowedFileTypes: ['image/*'],
    endpoint: '/api/v2/photos.json',
    fieldName: 'photo',
  },
  video: {
    allowedFileTypes: ['video/*'],
    endpoint: '/api/v2/videos/upload.json',
    fieldName: 'video',
  },
  audio: {
    allowedFileTypes: ['audio/*'],
    endpoint: '/api/v2/audio/upload.json',
    fieldName: 'audio',
  },
  sketch: {
    allowedFileTypes: ['image/*'],
    endpoint: '/api/v2/sketches.json',
    fieldName: 'sketch',
  },
  signature: {
    allowedFileTypes: ['image/*'],
    endpoint: '/api/v2/signatures.json',
    fieldName: 'signature',
  },
  attachment: {
    allowedFileTypes: ['*/*'],
    endpoint: '/api/v2/attachments.json',
    fieldName: 'attachment',
  },
};

/**

 * Configuration options for useMediaFieldUploader hook
 */
export interface MediaFieldUploaderConfig {
  mediaType: MediaType;
  currentItemCount: number;
  maxItems?: number;
  onAdd?: (metadata: MediaUploadMetadata, uploadedFile?: MediaUploadedFile) => void;
  onError?: (error: Error) => void;
  disabled?: boolean;
  allowedFileTypes?: string[];
  maxFileSize?: number;
  /** Maximum number of concurrent uploads (default: 3) */
  maxConcurrentUploads?: number;
}

export interface MediaFieldUploaderReturn {
  isMultiFileEnabled: boolean;
  /** Whether the uploader should be shown (not disabled and not at capacity) */
  shouldShowUploader: boolean;
  remainingSlots: number;
  endpoint: string;
  fieldName: string;
  allowedFileTypes: string[];
  maxFileSize?: number;
  maxConcurrentUploads: number;
  handleUploadSuccess: (metadata: MediaUploadMetadata, uploadedFile?: MediaUploadedFile) => void;
  handleUploadError: (error: Error) => void;
}

/**
 * Unified hook for managing media field uploads across all media types.
 * Provides configuration and helper functions for integrating with UniversalMediaUploader.
 *
 * This hook:
 * - Checks the multi-file-media-uploader feature flag
 * - Looks up media type settings from MEDIA_TYPE_SETTINGS
 * - Calculates remaining upload slots based on field max_length
 * - Provides upload handlers and restrictions
 *
 * @param config - Configuration options including media type and field constraints
 * @returns Upload state, settings, and handler functions
 *
 * @example
 * ```tsx
 * const uploader = useMediaFieldUploader({
 *   mediaType: 'photo',
 *   currentItemCount: photos.length,
 *   maxItems: field.max_length,
 *   onAdd: handlePhotoAdd,
 * });
 *
 * if (!uploader.shouldShowUploader) {
 *   return null;
 * }
 *
 * return (
 *   <UniversalMediaUploader
 *     endpoint={uploader.endpoint}
 *     allowedFileTypes={uploader.allowedFileTypes}
 *     maxNumberOfFiles={uploader.remainingSlots}
 *     onUploadSuccess={uploader.handleUploadSuccess}
 *   />
 * );
 * ```
 */
export const useMediaFieldUploader = ({
  mediaType,
  currentItemCount,
  maxItems = DEFAULT_MAX_FILES,
  onAdd,
  onError,
  disabled = false,
  allowedFileTypes: overrideAllowedFileTypes,
  maxFileSize,
  maxConcurrentUploads = 3,
}: MediaFieldUploaderConfig): MediaFieldUploaderReturn => {
  const isMultiFileEnabled = useMultiFileUploaderFlag();

  const settings = MEDIA_TYPE_SETTINGS[mediaType];

  const remainingSlots = Math.max(0, maxItems - currentItemCount);

  // Must have multifile enabled, uploading not be disabled, and not at capacity
  const shouldShowUploader = isMultiFileEnabled && !disabled && remainingSlots > 0;

  // Use override if provided, otherwise use settings
  const allowedFileTypes = overrideAllowedFileTypes ?? settings.allowedFileTypes;

  const handleUploadSuccess = useCallback(
    (metadata: MediaUploadMetadata, uploadedFile?: MediaUploadedFile) => {
      if (onAdd) {
        onAdd(metadata, uploadedFile);
      }
    },
    [onAdd],
  );

  const handleUploadError = useCallback(
    (error: Error) => {
      console.error('Upload error:', error);
      onError?.(error);
    },
    [onError],
  );

  return {
    isMultiFileEnabled,
    shouldShowUploader,
    remainingSlots,
    endpoint: settings.endpoint,
    fieldName: settings.fieldName,
    allowedFileTypes,
    maxFileSize,
    maxConcurrentUploads,
    handleUploadSuccess,
    handleUploadError,
  };
};

export default useMediaFieldUploader;
